import { useMemo } from 'react'
import { CheckCircle2, AlertTriangle, Mail, Phone, Users, MoreHorizontal, FileText } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ParticipanteEvento, StatusInscricao } from '../types'
import {
  METODO_PAGAMENTO_LABEL,
  STATUS_INSCRICAO_BADGE,
  STATUS_INSCRICAO_LABEL,
  STATUS_PAGAMENTO_BADGE,
  STATUS_PAGAMENTO_LABEL,
} from '../constants'
import { formatCurrency } from '@/lib/utils'
import { formatTime } from '../utils'
import { gerarUrlComprovante } from '@/services/participantes.service'
import { logger } from '@/lib/logger'

interface ParticipantsSectionProps {
  participantes: ParticipanteEvento[]
  onStatusChange: (id: string, status: StatusInscricao) => void
  onOpenPayment: (participante: ParticipanteEvento) => void
  onOpenCarne?: (participante: ParticipanteEvento) => void
  eventoGratuito?: boolean
  eventoPermiteParcelamento?: boolean
}

export function ParticipantsSection({
  participantes,
  onStatusChange,
  onOpenPayment,
  onOpenCarne,
  eventoGratuito = false,
  eventoPermiteParcelamento = false,
}: ParticipantsSectionProps) {
  const hasParticipantes = participantes.length > 0

  // Função para visualizar comprovante
  const handleVerComprovante = async (comprovanteUrl: string) => {
    try {
      // Se a URL parece ser um caminho (não começa com http), gerar URL assinada
      if (!comprovanteUrl.startsWith('http')) {
        const signedUrl = await gerarUrlComprovante(comprovanteUrl)
        window.open(signedUrl, '_blank')
      } else {
        // URL completa, abrir diretamente
        window.open(comprovanteUrl, '_blank')
      }
    } catch (error) {
      logger.error('Erro ao visualizar comprovante', { error, context: 'PARTICIPANTES_SECTION' })
    }
  }

  const mobileCards = useMemo(
    () =>
      participantes.map((participante) => (
        <div
          key={participante.id}
          className="rounded-xl border border-border bg-muted/30 p-4 shadow-sm"
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-base font-semibold">{participante.nome}</p>
                <p className="text-xs text-muted-foreground">{participante.funcoes.join(' • ')}</p>
              </div>
              <Badge className={STATUS_INSCRICAO_BADGE[participante.status]}>
                {STATUS_INSCRICAO_LABEL[participante.status]}
              </Badge>
            </div>

            <div className="grid gap-3 text-sm">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <Mail className="h-3.5 w-3.5" />
                <span>{participante.email}</span>
              </div>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <Phone className="h-3.5 w-3.5" />
                <span>{participante.telefone}</span>
              </div>
              {!eventoGratuito && (
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge className={STATUS_PAGAMENTO_BADGE[participante.pagamento.status]}>
                      {STATUS_PAGAMENTO_LABEL[participante.pagamento.status]}
                    </Badge>
                    {participante.pagamento.parcelasTotal && (
                      <span className="text-xs font-medium text-muted-foreground">
                        {participante.pagamento.parcelasPagas}/{participante.pagamento.parcelasTotal} parcelas
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {participante.pagamento.metodo && (
                      <span className="text-xs text-muted-foreground">
                        {METODO_PAGAMENTO_LABEL[participante.pagamento.metodo]} •{' '}
                        {formatCurrency(participante.pagamento.valor)}
                      </span>
                    )}
                    {!participante.pagamento.metodo && participante.pagamento.valor > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {formatCurrency(participante.pagamento.valor)}
                      </span>
                    )}
                    {participante.comprovanteUrl && (
                      <button
                        onClick={() => handleVerComprovante(participante.comprovanteUrl!)}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        Ver Comprovante
                      </button>
                    )}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 text-xs">
                {participante.checkIn.status === 'realizado' ? (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                    <span className="text-emerald-600">
                      Realizado{' '}
                      {participante.checkIn.horario &&
                        `• ${formatTime(participante.checkIn.horario)}`}
                    </span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <span className="text-muted-foreground">Pendente</span>
                  </>
                )}
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Abrir menu</span>
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onStatusChange(participante.id, 'confirmado')}>
                      Confirmar
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onStatusChange(participante.id, 'pendente')}>
                      Pendente
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onStatusChange(participante.id, 'cancelado')}>
                      Cancelar
                    </DropdownMenuItem>
                    {!eventoGratuito && (
                      <>
                        {/* Só mostrar "Registrar pagamento" se não tiver carnê (parcelas) */}
                        {!participante.pagamento.parcelasTotal && (
                          <DropdownMenuItem onClick={() => onOpenPayment(participante)}>
                            Registrar pagamento
                          </DropdownMenuItem>
                        )}
                        {eventoPermiteParcelamento && onOpenCarne && participante.pagamento.parcelasTotal && (
                          <DropdownMenuItem onClick={() => onOpenCarne(participante)}>
                            Ver Carnê
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          disabled={!participante.comprovanteUrl}
                          onClick={() => participante.comprovanteUrl && handleVerComprovante(participante.comprovanteUrl)}
                        >
                          Ver Comprovante
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
      )),
    [onOpenPayment, onOpenCarne, onStatusChange, participantes, eventoGratuito, eventoPermiteParcelamento, handleVerComprovante]
  )

  return (
    <Card>
      <CardHeader className="space-y-1">
        <CardTitle className="flex items-center gap-2 text-xl">
          <Users className="h-5 w-5 text-primary" />
          Participantes
        </CardTitle>
        <CardDescription>Acompanhe confirmações e pagamentos por pessoa</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="hidden md:block">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Participante</th>
                  <th className="px-3 py-2">Status</th>
                  {!eventoGratuito && <th className="px-3 py-2">Pagamento</th>}
                  <th className="px-3 py-2">Check-in</th>
                  <th className="px-3 py-2">Contato</th>
                  <th className="px-3 py-2">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {participantes.map((participante) => (
                  <tr key={participante.id} className="hover:bg-muted/40">
                    <td className="px-3 py-3">
                      <div className="flex flex-col">
                        <span className="font-medium">{participante.nome}</span>
                        <span className="text-xs text-muted-foreground">
                          {participante.funcoes.join(' • ')}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <Badge className={STATUS_INSCRICAO_BADGE[participante.status]}>
                        {STATUS_INSCRICAO_LABEL[participante.status]}
                      </Badge>
                    </td>
                    {!eventoGratuito && (
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <Badge className={STATUS_PAGAMENTO_BADGE[participante.pagamento.status]}>
                              {STATUS_PAGAMENTO_LABEL[participante.pagamento.status]}
                            </Badge>
                            {participante.comprovanteUrl && (
                              <button
                                onClick={() => handleVerComprovante(participante.comprovanteUrl!)}
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                                title="Ver comprovante"
                              >
                                <FileText className="h-3.5 w-3.5" />
                                Comprovante
                              </button>
                            )}
                          </div>
                          {participante.pagamento.parcelasTotal && (
                            <span className="text-xs font-medium text-muted-foreground">
                              {participante.pagamento.parcelasPagas}/{participante.pagamento.parcelasTotal} parcelas
                            </span>
                          )}
                          {participante.pagamento.metodo && (
                            <span className="text-xs text-muted-foreground">
                              {METODO_PAGAMENTO_LABEL[participante.pagamento.metodo]} •{' '}
                              {formatCurrency(participante.pagamento.valor)}
                            </span>
                          )}
                          {!participante.pagamento.metodo && participante.pagamento.valor > 0 && (
                            <span className="text-xs text-muted-foreground">
                              {formatCurrency(participante.pagamento.valor)}
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="px-3 py-3">
                      {participante.checkIn.status === 'realizado' ? (
                        <div className="flex items-center gap-1 text-xs text-emerald-600">
                          <CheckCircle2 className="h-4 w-4" />
                          Realizado{' '}
                          {participante.checkIn.horario &&
                            `• ${formatTime(participante.checkIn.horario)}`}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <AlertTriangle className="h-4 w-4" />
                          Pendente
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-muted-foreground">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <Mail className="h-3.5 w-3.5" />
                          <span>{participante.email}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5" />
                          <span>{participante.telefone}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Abrir menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => onStatusChange(participante.id, 'confirmado')}
                          >
                            Confirmar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onStatusChange(participante.id, 'pendente')}
                          >
                            Pendente
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onStatusChange(participante.id, 'cancelado')}
                          >
                            Cancelar
                          </DropdownMenuItem>
                          {!eventoGratuito && (
                            <>
                              {/* Só mostrar "Registrar pagamento" se não tiver carnê (parcelas) */}
                              {!participante.pagamento.parcelasTotal && (
                                <DropdownMenuItem onClick={() => onOpenPayment(participante)}>
                                  Registrar pagamento
                                </DropdownMenuItem>
                              )}
                              {eventoPermiteParcelamento && onOpenCarne && participante.pagamento.parcelasTotal && (
                                <DropdownMenuItem onClick={() => onOpenCarne(participante)}>
                                  Ver Carnê
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                disabled={!participante.comprovanteUrl}
                                onClick={() => participante.comprovanteUrl && handleVerComprovante(participante.comprovanteUrl)}
                              >
                                Ver Comprovante
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="space-y-3 md:hidden">{mobileCards}</div>
        {!hasParticipantes && (
          <p className="text-sm text-muted-foreground">
            Nenhum participante cadastrado ainda. Utilize o botão &quot;Incluir participante&quot;
            para adicionar inscritos ao evento.
          </p>
        )}
      </CardContent>
    </Card>
  )
}
